import React, { useState, useEffect, useMemo, useRef } from "react";
import useApi from "../../hooks/useApiGetPost";
import {
  Chip,
  Button,
  Input,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  CircularProgress,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  TableFooter,
  Autocomplete,
  TextField,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Switch,
} from "@mui/material";

// Placeholder imports (assuming these exist in the actual project structure)
import useApiAthul from "../../hooks/useApi";
import groupSalesThreeLevels from "../utils/groupSalesThreeLevels";

// 🚨 NEW COMPONENT IMPORTS

import PurchaseAnalysisReportPDF from "./PurchaseAnalysisReportPDF";

import { PDFDownloadLink, pdf } from "@react-pdf/renderer";
import ReportDownloadButton from "../sales_analysis_report/ReportDownloadButton";
import DynamicTableForPurchaseAnalysis from "./DynamicTableForPurchaseAnalysis";

let BASE_URL = "https://cubixweberp.com:213";
const API_PATHS = localStorage.getItem("apiPaths")
  ? JSON.parse(localStorage.getItem("apiPaths"))
  : [{ API_CONFIG: "https://cubixweberp.com:213" }];
const COMPANY_CODE = localStorage.getItem("companyCode") || "-";

// console.log("API_PATHS -->", API_PATHS);
if (API_PATHS) {
  if (API_PATHS?.length > 0) {
    BASE_URL = API_PATHS[0].API_CONFIG;
  }
}

// --- Utility Functions (Keep these in your main component or move to a shared file) ---

const formatNumber = (val) =>
  Number(val || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// The keys in your data are likely 'PROFIT', 'COST', 'QTY', 'VAT_AMT', etc.
// Ensure this utility function covers all numeric keys you want to total.
const isNumericHeader = (header) =>
  [
    "AMT",
    "COMMISSION",
    "RETURN",
    "VAT",
    "TOTAL",
    "PROFIT",
    "COST",
    "QTY",
    "AMOUNT",
    "SRNO",
    "VAT_AMT",
    "VATAMT",
    "VAT AMT",
    "TOTAL_AMT",
    "GROSS SALE",
    "DISC",
    "NET SALES",
    "EXCL_VAT_AMOUNT",
  ].some((s) => header.toUpperCase().includes(s));

// --- Helper Functions (Stubs for the original dynamic logic) ---

// Placeholder for logic that determines the key for the first column in aggregated modes
const getDescriptiveColumnKey = (mode, data) => {
  if (!data || data.length === 0) return "CODE";

  // In non-grouped/non-aggregated modes (like INVPROF or ITMPROF),
  // the first column might be the Invoice No or Item Code.
  if (mode === "SALES_CUSTOMER") return "ACCOUNT";
  if (mode === "SALES_SALESMAN") return "SALESPERSON";

  // Find the first key that is NOT a numeric header and NOT an internal grouping key
  const descriptiveKey = Object.keys(data[0]).find(
    (key) =>
      !isNumericHeader(key) &&
      !["isGroupHeader", "isSubHeader", "totalAmount"].includes(key),
  );

  // Fallback to the first non-numeric column found, or a default.
  return (
    descriptiveKey ||
    Object.keys(data[0]).find((key) => !isNumericHeader(key)) ||
    "CODE"
  );
};

// Placeholder for grandColumnTotals calculation (kept for PDF usage, but not passed to DynamicTable)
const calculateGrandTotals = (data) => {
  if (!data || data.length === 0) return {};
  const totals = {};
  const firstRow = data[0];

  Object.keys(firstRow).forEach((key) => {
    // Simple check: if the key contains numbers in the first row, aggregate it
    if (typeof firstRow[key] === "number" || isNumericHeader(key)) {
      totals[key] = data.reduce((sum, row) => sum + Number(row[key] || 0), 0);
    }
  });
  return totals;
};

// Placeholder for page and rowsPerPage logic (if you decide to implement this in the parent)
const usePagination = (data) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(1000);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Slice the data for the current page
  const paginatedRows = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return {
    paginatedRows,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  };
};

const useScrollLoading = (data) => {
  // 25 is the default number of rows to load at once
  const defaultRowsPerPage = 1000;
  const [rowsToDisplay, setRowsToDisplay] = useState(defaultRowsPerPage);

  // Function to load the next set of data
  const loadNextPage = () => {
    setRowsToDisplay((prev) =>
      Math.min(prev + defaultRowsPerPage, data.length),
    );
  };

  // Reset the loaded count whenever the underlying filtered data array changes
  useEffect(() => {
    // Reset rows to display if the data is available, otherwise set to 0.
    setRowsToDisplay(data.length > 0 ? defaultRowsPerPage : 0);
  }, [data.length]);

  const paginatedRows = data.slice(0, rowsToDisplay);
  const hasMore = rowsToDisplay < data.length;

  return { paginatedRows, loadNextPage, hasMore, totalRows: data.length };
};

const PurchaseAnalysisReport = () => {
  const COMPANY_CODE = localStorage.getItem("companyCode");
  const [text_search_customer, setTextSearchCustomer] = useState("");

  const [searchTermCustomer, setSearchTerm] = useState("");

  const { request, loading } = useApiAthul();

  const [current_statusOfReturnCheckbox, setCurrentStatusOfReturnCheckbox] =
    useState("NORET");
  const [current_statusOfFifoCheckbox, setCurrentStatusOfFifoCheckbox] =
    useState(false);

  const [showItemList, setShowItemList] = useState(false);

  // --- State Variables ---
  const [searchTextTable, setSearchTextTable] = useState("");
  const [currentSelectedItem, setCurrentSelectedItem] = useState("-");
  const [modalSuggestions, setModalSuggestions] = useState([]);
  const [searchBy, setSearchBy] = useState("UNIVERSAL");

  const salesModes = [
    // { label: "Purchase Supplier", value: "PURCHASE_SUPPLIER" },
    { label: "Supplierwise purchase", value: "PURCHASE_SUPPLIER" },

    { label: "Purchase List", value: "PURCHASELIST" },

    { label: "Purchase Local", value: "PURCHASE_LOCAL" },

    { label: "Purchase Import", value: "PURCHASE_IMPORT" },

    { label: "FOC Items", value: "PURCHASE_ITEMWISEFOC" }, //

    { label: "Itemwise Purchase", value: "PURCHASE_ITEMWISE" },

    { label: "Purchase Requistion", value: "PURCHASE_REQUEST" }, //

    { label: "Purchase Return List", value: "PURCHASERETURNLIST" },

    { label: "Itemwise Purchase Return", value: "PURCHASERETURN_ITEMWISE" },

    { label: "Purchase Register", value: "PURCHASEREGISTER" },

    { label: "Monthwise Purchase", value: "PURCHASE_MONTH" },

    { label: "Itemwise Summary", value: "PURCHASE_ITEMWISE_SUMMARY" },

    { label: "Monthwise Group", value: "PURCHASE_GROUP_MONTH" },

    // { label: "Non stock purchase", value: "NONSTOCK_PURCHASE" },//not needed removed
  ];

  const [currentSelectedModeObject, setCurrentSelectedModeObject] =
    useState(null);
  const [currentSelectedDepartment, setCurrentSelectedDepartment] =
    useState("-");
  const [userDepartmentList, setUserDepartment] = useState(null);
  const [currentSelectedCategory, setCurrentSelectedCategory] = useState("-");
  const [currentSelectedSubCategory, setCurrentSelectedSubCategory] =
    useState("-");
  const [currentSelectedGroup, setCurrentSelectedGroup] = useState("-");
  const [currentSelectedSubGroup, setCurrentSelectedSubGroup] = useState("-");

  const {
    loading: loadinggetSubCategory,
    data: datagetSubCategory,
    get: getSubCategory,
  } = useApi();
  const {
    loading: loadinggetCategory,
    data: datagetCategory,
    get: getCategory,
  } = useApi();
  const {
    loading: loadinggetSubgroup,
    data: datagetSubgroup,
    get: getSubgroup,
  } = useApi();
  const {
    loading: loadinggetgroup,
    data: datagetgroup,
    get: getgroup,
  } = useApi();
  const {
    loading: loadinggetCustomerList,
    data: datagetCustomerList,
    get: getCustomerList,
  } = useApi();

  const [customerListData, setCustomerListData] = useState(null);

  useEffect(() => {
    if (datagetCustomerList) {
      setCustomerListData(datagetCustomerList);
    }
  }, [datagetCustomerList]);

  const {
    data: datagetSalesOrderReportAPI,
    loading: loadinggetSalesOrderReportAPI,
    get: getSalesOrderReportAPI,
  } = useApi();
  const { data: datagetgetFinancialDateApi, get: getFinancialDateApi } =
    useApi();
  const { data: datagetJobList, get: getJobList } = useApi();
  const { data: datagetSalesmanList, get: getSalesmanList } = useApi();

  const [isFilterPanelVisible, setIsFilterPanelVisible] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  const [jobCode, setJobCode] = useState(null);
  const [chipSearch, setChipSearch] = useState("");
  const [selectedSalesMan, setSelectedSalesMan] = useState("");

  // --- Effects & Handlers ---

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    setUserDepartment(currentUser || null);

    setCurrentSelectedDepartment(
      currentUser?.[0]?.DEPTNO ? currentUser[0].DEPTNO : "-",
    );

    const today = new Date().toLocaleDateString("en-CA");
    setDateFrom(today);
    setDateTo(today);

    getCategory(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/category/-/-`,
    );
    getSubCategory(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/subcategory/-/-`,
    );
    getgroup(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/group/-/-`,
    );
    getSubgroup(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/subgroup/-/-`,
    );
    getJobList(
      `${API_PATHS[0].API_CONFIG}/api/cpayscount/${COMPANY_CODE}/joblist/-/-`,
    );

    if (text_search_customer?.trim()?.length != 0) {
      const currentUser = JSON.parse(localStorage.getItem("currentUser"));

      let DEPTNO = currentUser?.[0]?.DEPTNO ? currentUser[0].DEPTNO : "-";
      getCustomerList(
        `${API_PATHS[0].API_CONFIG}/api/Search_Customer/${COMPANY_CODE}/Cust/${text_search_customer}/${DEPTNO}`,
      );
    }

    let departmentToPassToAPI = "-";

    if (currentSelectedDepartment) {
      departmentToPassToAPI = currentSelectedDepartment;
    }

    getSalesmanList(
      `${API_PATHS[0].API_CONFIG}/api/MasterCount/${COMPANY_CODE}/SALESPERSONLIST/-/${departmentToPassToAPI}`,
    );

    handleChipClick({ label: "Purchase Supplier", value: "PURCHASE_SUPPLIER" });
  }, []);

  const handleChipClick = (chip) => {
    setCurrentSelectedModeObject(chip);
    setSelectedFilters((prev) => (prev.includes(chip) ? [] : [chip]));
  };

  const resetFilters = () => {
    // FINANCIAL YEAR START DATE LOGIC
    if (datagetgetFinancialDateApi) {
      const fyStart = datagetgetFinancialDateApi[0]?.fystart?.split("T")[0];
      setDateFrom(fyStart);
    }

    const today = new Date().toLocaleDateString("en-CA");
    setDateTo(today);

    handleChipClick({ label: "Invoice wise Profit", value: "INVPROF" });
    setItemSearch("");
    setCurrentSelectedGroup("-");
    setCurrentSelectedSubGroup("-");
    setCurrentSelectedCategory("-");
    setCurrentSelectedSubCategory("-");
    setJobCode(null);
    setChipSearch("");
  };

  const filteredChips = salesModes.filter((chip) =>
    chip.label.toLowerCase().includes(chipSearch.toLowerCase()),
  );

  useEffect(() => {
    if (
      currentSelectedModeObject &&
      dateFrom &&
      dateTo &&
      currentSelectedDepartment
    ) {
      let mode_to_pass_to_api = currentSelectedModeObject
        ? currentSelectedModeObject.value
        : "-";
      let job_code_to_pass_to_api = jobCode ? jobCode.JobCode : "-";
      let customer_account_to_api =
        currentSelectedItem != "-" ? currentSelectedItem?.account?.trim() : "-";

      let ret_status_for_api = current_statusOfReturnCheckbox;

      let fifo_status_for_api = "N";
      if (current_statusOfFifoCheckbox === true) {
        fifo_status_for_api = "N";
      } else {
        fifo_status_for_api = "N"; // what to pass - or anything else now put N just for testing
      }

      //let apiUrl = `${API_PATHS[0].API_CONFIG}/api/CRMPurchaseAnalysisrep/${COMPANY_CODE}/${mode_to_pass_to_api}/${dateFrom}/${dateTo}/${currentSelectedGroup}/${currentSelectedSubGroup}/${currentSelectedCategory}/${currentSelectedSubCategory}/-/-/${ret_status_for_api}/${currentSelectedDepartment}/${customer_account_to_api}/-/${fifo_status_for_api}`

      let apiUrl = `${API_PATHS[0].API_CONFIG}/api/CRMPurchaseAnalysisrep/${COMPANY_CODE}/${mode_to_pass_to_api}/-/-/${dateFrom}/${dateTo}/-/${currentSelectedCategory}/${currentSelectedGroup}/${currentSelectedDepartment}/${currentSelectedSubGroup}/${currentSelectedSubCategory}`;

      getSalesOrderReportAPI(apiUrl);
    }
  }, [
    currentSelectedModeObject,
    dateFrom,
    dateTo,
    currentSelectedDepartment,
    currentSelectedCategory,
    currentSelectedGroup,
    currentSelectedSubGroup,
    currentSelectedSubCategory,
    currentSelectedItem,
    jobCode,
    current_statusOfReturnCheckbox,
    current_statusOfFifoCheckbox,
  ]);

  useEffect(() => {
    getFinancialDateApi(
      `${API_PATHS[0].API_CONFIG}/api/Cpayscount/${COMPANY_CODE}/companyinfo/-/-`,
    );
  }, []);

  // FINANCIAL YEAR START DATE LOGIC
  useEffect(() => {
    if (datagetgetFinancialDateApi) {
      const fyStart = datagetgetFinancialDateApi[0]?.fystart?.split("T")[0];
      setDateFrom(fyStart);
    }
  }, [datagetgetFinancialDateApi]);

  const fetchProductData = async (
    query = "-",
    searchByValue = searchBy,
    location = "A",
  ) => {
    const formattedQuery = query.replace(/\//g, "%2F");
    try {
      const url = `${BASE_URL}/api/CRMStockList/${COMPANY_CODE}/${searchByValue}/${formattedQuery}/${location}/-`;
      const response = await request({ url, method: "GET", timeout: 5000 });
      return response || [];
    } catch (err) {
      console.error("Failed to fetch product data", err);
      return [];
    }
  };

  const getInitialProductData = async () => {
    setModalSuggestions(await fetchProductData("-"));
  };
  useEffect(() => {
    getInitialProductData();
  }, []);

  const handleModalSearchQueryChange = async (query = "-") => {
    setItemSearch(query);
    setModalSuggestions(await fetchProductData(query));
  };

  useEffect(() => {
    if (searchTermCustomer?.length > 0) {
      //handleModalSearchQueryChange(itemSearch.trim());

      const currentUser = JSON.parse(localStorage.getItem("currentUser"));

      let DEPTNO = currentUser?.[0]?.DEPTNO ? currentUser[0].DEPTNO : "-";

      getCustomerList(
        `${API_PATHS[0].API_CONFIG}/api/Search_Customer/${COMPANY_CODE}/Cust/${searchTermCustomer}/${DEPTNO}`,
      );
    } else {
      setCurrentSelectedItem("-");
    }
  }, [searchTermCustomer]);

  const getTotalColumns = (data) => {
    if (!data || data.length === 0) return 1; // Default to 1 if no data

    // Dynamically determine all unique keys from the first row
    const allKeys = Object.keys(data[0]);

    // Exclude internal/helper keys used during grouping/aggregation
    const descriptiveColumnKey = getDescriptiveColumnKey(
      currentSelectedModeObject?.value,
      data,
    );

    const excludedKeys = [
      "isGroupHeader",
      "isSubHeader",
      "isSubSubHeader",
      "totalAmount",
      "totalAmountExclVAT",
      descriptiveColumnKey,
    ];

    // Count the descriptive column (1) + all other data columns
    const displayKeys = allKeys.filter((key) => !excludedKeys.includes(key));

    // The total is the sticky column + the rest of the dynamic columns
    return 1 + displayKeys.length;
  };

  // --- Core Logic (Grouping, Filtering, Totals) ---

  const reportData = datagetSalesOrderReportAPI || [];

  // The data passed to the child component must be the final, filtered/grouped array.
  const filteredRows = useMemo(() => {
    console.log("reportData inside  filteredRows---->", reportData);

    // 2. Simple Filtering (for all other modes)
    // Apply your existing searchTextTable filter logic here if needed, or pass the full data.
    let finalData = reportData;

    if (searchTextTable) {
      const search = searchTextTable.toLowerCase();
      finalData = reportData.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(search),
        ),
      );
    }

    return finalData;
  }, [reportData, currentSelectedModeObject, searchTextTable]);

  // Calculate the array of keys that should be totaled in the footer.
  const keysToTotal = useMemo(() => {
    if (!reportData || reportData.length === 0) return [];

    // Get all keys from the first row
    const firstRow = reportData[0];
    const allKeys = Object.keys(firstRow);

    // Filter keys based on the existing numeric header logic (now including PROFIT, COST, QTY)
    // Ensure keys are filtered correctly.
    return allKeys.filter((key) => isNumericHeader(key));
  }, [reportData]);

  // Calculate the primary descriptive key (used for the first column and footer alignment)
  const descriptiveColumnKey = useMemo(() => {
    // Recalculate the descriptive key based on the final data, not just the raw reportData
    return getDescriptiveColumnKey(
      currentSelectedModeObject?.value,
      filteredRows,
    );
  }, [currentSelectedModeObject, filteredRows]);

  // Calculate totals only on the raw report data or final filtered data (Kept for PDF download logic)
  const grandColumnTotals = useMemo(() => {
    console.log(
      "calculateGrandTotals(reportData)",
      calculateGrandTotals(reportData),
    );
    return calculateGrandTotals(reportData);
  }, [reportData]);

  // --- Component Mapping ---

  const ReportTableMap = useMemo(
    () => ({
      // 1. GROUPED MODES (Hierarchical 3-level)
      INVPROF: DynamicTableForPurchaseAnalysis,
      ITMPROF: DynamicTableForPurchaseAnalysis,
    }),
    [],
  );

  const clickedOnPrint = async () => {
    if (!filteredRows || filteredRows?.length === 0) return;
    if (!PurchaseAnalysisReportPDF) return;

    // Open a blank window first to avoid popup block
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Popup blocked! Please allow popups for this site.");
      return;
    }

    // Render your PDF component
    const doc = (
      <PurchaseAnalysisReportPDF
        rows={filteredRows}
        descriptiveColumnKey={descriptiveColumnKey}
        grandColumnTotals={grandColumnTotals}
        currentSelectedModeObject={currentSelectedModeObject}
        clientDetails={{
          name: "test",
          address: "test address",
          phone: "1234567890",
          email: "test@email",
          footerText: "test footer",
        }}
      />
    );

    // Generate PDF as blob
    const asPdf = pdf(doc);
    const blob = await asPdf.toBlob();

    // Create blob URL
    const blobUrl = URL.createObjectURL(blob);

    // Load the PDF in the blank window
    printWindow.location.href = blobUrl;

    // Optional: wait until the PDF loads, then print
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const CurrentTableComponent = currentSelectedModeObject
    ? DynamicTableForPurchaseAnalysis
    : null;

  // --- Pagination (Remains in Parent) ---
  // const { paginatedRows, page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination(filteredRows);

  const { paginatedRows, loadNextPage, hasMore, totalRows } =
    useScrollLoading(filteredRows);

  // const scrollContainerRef = useRef(null);

  // // --- Scroll Handler ---
  // const handleScroll = () => {

  //     console.log("handleScroll outside  --->")

  //     const currentTarget = scrollContainerRef.current;

  //     // Only trigger if we aren't already loading and there is more data to load
  //     if (!currentTarget || loadinggetSalesOrderReportAPI || !hasMore) return;

  //     const { scrollTop, scrollHeight, clientHeight } = currentTarget;

  //     console.log("handleScroll inside --->")

  //     // Check if user scrolled within 1px of the bottom
  //     if (scrollHeight - scrollTop <= clientHeight + 10) {
  //         loadNextPage();
  //     }
  // };

  const totalColumnsCount = getTotalColumns(paginatedRows); // Calculate the number

  const clickedOnExport = async () => {
    // Check if running on the client (browser) before attempting DOM operations
    if (typeof document === "undefined") {
      console.warn(
        "PDF Export called in a non-browser environment (SSR). Skipping download.",
      );
      return;
    }

    // 1. Render the PDF Document instance using the imported component
    const documentInstance = // Renamed to avoid confusion with browser's 'document'
      (
        <PurchaseAnalysisReportPDF
          rows={filteredRows}
          descriptiveColumnKey={descriptiveColumnKey}
          clientDetails={{
            name: "test",
            address: "test address",
            phone: "1234567890",
            email: "test@email",
            footerText: "test footer",
          }}
        />
      );

    try {
      // 2. Use the pdf() utility to generate the blob
      const blob = await pdf(documentInstance).toBlob();

      // 3. Create a temporary URL and link element to trigger the download
      const url = URL.createObjectURL(blob);

      // Use a single, correct declaration of 'link' (DOM operation is safe here)
      const link = document.createElement("a");

      // Define the file name
      link.download = `SalesReport_${new Date().toISOString().split("T")[0]}.pdf`;
      link.href = url;

      // 4. Append to body, click, and clean up
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url); // Clean up the URL object
    } catch (error) {
      console.error("Error during PDF generation or download:", error);
    }
  };

  return (
    <div style={{ height: "calc(100vh - 200px)" }}>
      {/* Header panel Omitted for Brevity */}
      <div className="relative overflow-hidden rounded-xl shadow-md mx-4 my-1">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-blue-500 to-green-500 opacity-90" />
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: "repeat",
          }}
        />
        <div className="relative px-6 py-6 sm:px-10 flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white drop-shadow-md">
              Purchase Analysis Report
            </h1>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={clickedOnPrint}
              className="flex items-center px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-md text-sm font-medium transition duration-200 backdrop-blur-sm"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.8"
                stroke="currentColor"
                className="w-5 h-5 mr-1"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 9V2h12v7m-9 8h6m-9 4h12v-4H6v4zm-1 0a2 2 0 0 1-2-2V11a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5z"
                />
              </svg>
              Print
            </button>
            {/* <button
                            onClick={clickedOnExport}
                            className="flex items-center px-3 py-2 bg-white hover:bg-gray-100 text-gray-800 rounded-md text-sm font-medium transition duration-200"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="currentColor" className="w-5 h-5 mr-1 text-indigo-600"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-4-4-4 4m0 0-4-4m4 4V4" /></svg>
                            Export
                        </button> */}
            {currentSelectedModeObject && filteredRows && (
              <ReportDownloadButton
                ReportPDF={PurchaseAnalysisReportPDF}
                currentSelectedModeObject={currentSelectedModeObject}
                filteredRows={filteredRows}
                descriptiveColumnKey={descriptiveColumnKey}
                grandColumnTotals={grandColumnTotals}
                clientDetails={{
                  name: "test",
                  address: "test address",
                  phone: "1234567890",
                  email: "test@email",
                  footerText: "test footer",
                }}
              >
                Download
              </ReportDownloadButton>
            )}
          </div>
        </div>
      </div>

      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            width: "18%",
            maxWidth: "260px",
            padding: 2,
            borderRight: "1px solid #ccc",
            backgroundColor: "#f9f9f9",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <div className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-1.5 shadow-sm hover:shadow-md transition-all duration-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 mb-3">
            <svg
              className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.7955 15.8111L21 21M18 10.5C18 14.6421 14.6421 18 10.5 18C6.35786 18 3 14.6421 3 10.5C3 6.35786 6.35786 3 10.5 3C14.6421 3 18 6.35786 18 10.5Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              placeholder="Search modes"
              value={chipSearch}
              onChange={(e) => setChipSearch(e.target.value)}
              className="w-full bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400 focus:ring-0"
            />
          </div>
          <div
            style={{
              flexGrow: 1,
              overflowY: "auto",
              maxHeight: "calc(100vh - 300px)",
              paddingRight: "4px",
            }}
            className="scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-500 transition-all duration-300"
          >
            {filteredChips.map((chip, index) => (
              <div key={index}>
                <Chip
                  title={chip.label}
                  label={chip.label}
                  clickable
                  color={
                    selectedFilters.filter(
                      (item) => item.value === chip.value,
                    )[0]?.value
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleChipClick(chip)}
                  sx={{
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    height: "auto",
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      textAlign: "left",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block",
                      width: "100%",
                      paddingTop: "2px",
                      paddingBottom: "2px",
                    },
                  }}
                />
              </div>
            ))}
          </div>
        </Box>

        {/* Right Panel (Main Content & Table) */}
        <Box
          sx={{
            width: "82%",
            paddingLeft: 3,
            paddingRight: 3,
            paddingTop: 2,
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Paper
            sx={{ padding: 1, borderRadius: "8px", backgroundColor: "#fff" }}
          >
            {/* Top Filters Omitted for Brevity */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div
                style={{
                  display: "flex",
                  marginLeft: "10px",
                  marginTop: "5px",
                }}
              >
                <Grid item>
                  <Input
                    fullWidth
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </Grid>
                <Grid item style={{ marginLeft: "10px" }}>
                  <Input
                    fullWidth
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </Grid>
              </div>

              <div style={{ display: "flex", marginTop: "5px" }}>
                <Grid item>
                  <Select
                    fullWidth
                    value={currentSelectedDepartment}
                    onChange={(e) =>
                      setCurrentSelectedDepartment(e.target.value)
                    }
                    displayEmpty
                    sx={{
                      height: 36,
                      fontSize: 14,
                      "& .MuiSelect-select": { paddingY: 0.5 },
                    }}
                  >
                    <MenuItem value="-">Select Department</MenuItem>
                    {userDepartmentList?.map((group, index) => (
                      <MenuItem key={index} value={group.DEPTNO?.trim()}>
                        {group.DEPTNO?.trim()}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                {/* <Grid item style={{ marginLeft: "10px" }}>
                                    <Input fullWidth placeholder="Item" onKeyDown={(e) => {
                                        if (e.key === "F2") { e.preventDefault(); setShowItemList(true); }
                                    }} value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} />
                                    {
                                        itemSearch?.length > 0 && modalSuggestions?.length > 0 &&
                                        <div style={{ position: "absolute", zIndex: 10, height: "200px", width: "200px", overflowY: "scroll", backgroundColor: "#f7f7f7" }}>
                                            {modalSuggestions?.map((item, i) => {
                                                return (<p key={i} onClick={() => { setCurrentSelectedItem(item); setItemSearch(item.Code); setModalSuggestions(null) }} style={{ padding: "10px", border: "1px solid #dedede" }}>{item.Code}</p>)
                                            })}
                                        </div>
                                    }
                                </Grid> */}

                {/* <Grid item style={{ marginLeft: "10px" }}>
                                    <Input fullWidth placeholder="Search Customer" value={text_search_customer} onChange={(e) =>{ setTextSearchCustomer(e.target.value);setSearchTerm(e.target.value); }} />
                                    {
                                        (customerListData && text_search_customer?.length > 0) ?
                                            text_search_customer?.length > 0 && customerListData?.length > 0 &&
                                            <div style={{ position: "absolute", zIndex: 10, height: "200px", width: "200px", overflowY: "scroll", backgroundColor: "#f7f7f7" }}>
                                                {customerListData?.map((item, i) => {
                                                    return (<p key={i} onClick={() => { setCurrentSelectedItem(item);setTextSearchCustomer(item.Custname) ; setCustomerListData(null) }} style={{ padding: "10px", border: "1px solid #dedede" }}>{item.Custname}</p>)
                                                })}
                                            </div>
                                            :
                                            null
                                    }
                                </Grid> */}
              </div>

              <div style={{ display: "flex", justifyContent: "end" }}>
                <Box sx={{ marginRight: 3, display: "flex" }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={resetFilters}
                    sx={{
                      minWidth: "auto",
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                      lineHeight: 1,
                      borderRadius: "4px",
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setIsFilterPanelVisible((prev) => !prev)}
                    sx={{
                      marginLeft: "10px",
                      minWidth: "auto",
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                      lineHeight: 1,
                      borderRadius: "4px",
                    }}
                  >
                    Filters
                  </Button>
                </Box>
              </div>
            </div>

            {/* Expanded Filters */}
            {isFilterPanelVisible && (
              <div>
                <Grid container spacing={2} sx={{ marginTop: 1 }}>
                  {/* Group Autocomplete */}
                  <Grid item>
                    <Box sx={{ width: 200 }}>
                      <Autocomplete
                        options={datagetgroup || []}
                        getOptionLabel={(option) => option?.DESCRIPTION || ""}
                        loading={loadinggetgroup}
                        value={
                          datagetgroup?.find(
                            (g) => g["GRP CODE"] === currentSelectedGroup,
                          ) || null
                        }
                        onChange={(e, newValue) =>
                          setCurrentSelectedGroup(
                            newValue ? newValue["GRP CODE"] : "-",
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Group"
                            placeholder="Type to search..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadinggetgroup ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              "& .MuiInputBase-input": {
                                fontSize: 13,
                                padding: "4px 8px",
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Grid>

                  {/* SubGroup Autocomplete */}
                  <Grid item>
                    <Box sx={{ width: 200 }}>
                      <Autocomplete
                        options={datagetSubgroup || []}
                        getOptionLabel={(option) => option?.DESCRIPTION || ""}
                        loading={loadinggetSubgroup}
                        value={
                          datagetSubgroup?.find(
                            (g) => g["GRP CODE"] === currentSelectedSubGroup,
                          ) || null
                        }
                        onChange={(e, newValue) =>
                          setCurrentSelectedSubGroup(
                            newValue ? newValue["GRP CODE"] : "-",
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select SubGroup"
                            placeholder="Type to search..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadinggetSubgroup ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              "& .MuiInputBase-input": {
                                fontSize: 13,
                                padding: "4px 8px",
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Grid>

                  {/* Category Autocomplete */}
                  <Grid item>
                    <Box sx={{ width: 200 }}>
                      <Autocomplete
                        options={datagetCategory || []}
                        getOptionLabel={(option) => option?.DESCRIPTION || ""}
                        loading={loadinggetCategory}
                        value={
                          datagetCategory?.find(
                            (g) => g["GRP CODE"] === currentSelectedCategory,
                          ) || null
                        }
                        onChange={(e, newValue) =>
                          setCurrentSelectedCategory(
                            newValue ? newValue["GRP CODE"] : "-",
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Category"
                            placeholder="Type to search..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadinggetCategory ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              "& .MuiInputBase-input": {
                                fontSize: 13,
                                padding: "4px 8px",
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Grid>

                  {/* SubCategory Autocomplete */}
                  <Grid item>
                    <Box sx={{ width: 200 }}>
                      <Autocomplete
                        options={datagetSubCategory || []}
                        getOptionLabel={(option) => option?.DESCRIPTION || ""}
                        loading={loadinggetSubCategory}
                        value={
                          datagetSubCategory?.find(
                            (g) => g["GRP CODE"] === currentSelectedSubCategory,
                          ) || null
                        }
                        onChange={(e, newValue) =>
                          setCurrentSelectedSubCategory(
                            newValue ? newValue["GRP CODE"] : "-",
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select SubCategory"
                            placeholder="Type to search..."
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadinggetSubCategory ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              "& .MuiInputBase-input": {
                                fontSize: 13,
                                padding: "4px 8px",
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Grid>

                  {/* JobCode (already Autocomplete) */}
                  <Grid item>
                    <Box sx={{ width: 200 }}>
                      <Autocomplete
                        options={datagetJobList || []}
                        getOptionLabel={(option) =>
                          option?.JobCode ? `${option.Description || ""}` : ""
                        }
                        value={jobCode}
                        onChange={(event, newValue) => setJobCode(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Job"
                            placeholder="Type to search..."
                            size="small"
                            sx={{
                              "& .MuiInputBase-input": {
                                fontSize: 13,
                                padding: "4px 8px",
                              },
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Typography
                              variant="body2"
                              sx={{ color: "#000000" }}
                            >
                              {option.Description}
                            </Typography>
                          </Box>
                        )}
                        isOptionEqualToValue={(option, value) =>
                          option.JobCode === value.JobCode
                        }
                      />
                    </Box>
                  </Grid>

                  {/* Salesman (already Autocomplete) */}
                  <Grid item>
                    <Box sx={{ width: 200 }}>
                      <Autocomplete
                        options={datagetSalesmanList || []}
                        getOptionLabel={(option) =>
                          option?.JobCode ? `${option.Description || ""}` : ""
                        }
                        value={selectedSalesMan}
                        onChange={(event, newValue) =>
                          setSelectedSalesMan(newValue)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Salesman"
                            placeholder="Type to search..."
                            size="small"
                            sx={{
                              "& .MuiInputBase-input": {
                                fontSize: 13,
                                padding: "4px 8px",
                              },
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Typography
                              variant="body2"
                              sx={{ color: "#000000" }}
                            >
                              {option.Description}
                            </Typography>
                          </Box>
                        )}
                        isOptionEqualToValue={(option, value) =>
                          option.JobCode === value.JobCode
                        }
                      />
                    </Box>
                  </Grid>

                  <RadioGroup
                    sx={{ padding: 2 }}
                    row
                    value={current_statusOfReturnCheckbox}
                    onChange={(e) =>
                      setCurrentStatusOfReturnCheckbox(e.target.value)
                    }
                    name="sales-filter"
                  >
                    <FormControlLabel
                      value="NORET"
                      control={<Radio />}
                      label="Sales Only"
                    />
                    <FormControlLabel
                      value="RET"
                      control={<Radio />}
                      label="Include Return"
                    />
                  </RadioGroup>

                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ padding: "10px" }}>FIFO</span>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={current_statusOfFifoCheckbox}
                          onChange={(e) =>
                            setCurrentStatusOfFifoCheckbox(e.target.checked)
                          }
                        />
                      }
                      label={current_statusOfFifoCheckbox ? "ON" : "OFF"}
                    />
                  </div>
                  {/* Group View Checkbox */}
                  {/* <Grid item>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={groupViewCheckbox}
                                                    onChange={(e) => setGroupViewCheckbox(e.target.checked)}
                                                />
                                            }
                                            label="Group View"
                                        />
                                    </Grid> */}
                </Grid>
              </div>
            )}
          </Paper>

          {/* Table Section */}
          <Paper
            sx={{
              width: "100%",
              marginTop: 2,
              // 4. 🚨 CRITICAL FIX: This container must take the remaining vertical space
              //    and control the flow of its children.
              flexGrow: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* 🚨 Dynamic Table Rendering Block */}
            <Box
              // ref={scrollContainerRef}
              sx={{
                p: 1,
                flexGrow: 1, // Take all available height
                overflowY: "auto", // Enable vertical scrolling here
                minHeight: 0, // Crucial for flex scrolling
              }}
              // onScroll={handleScroll} // 🚨 ADD SCROLL HANDLER
            >
              {CurrentTableComponent ? (
                <CurrentTableComponent
                  // Pass all necessary data and helpers to the selected component
                  rows={paginatedRows} // Use paginated rows for the component
                  loading={loadinggetSalesOrderReportAPI}
                  mode={currentSelectedModeObject.value} // Required by some children
                  // 🚀 FIX: Pass the array of column keys to total
                  footerTotalKeys={keysToTotal}
                  // Props specific to certain types
                  groupSalesThreeLevels={groupSalesThreeLevels} // For Grouped
                  descriptiveColumnKey={descriptiveColumnKey} // For Aggregated
                  totalColumnsCount={totalColumnsCount}
                  hasMore={hasMore}
                  loadNextPage={loadNextPage}
                />
              ) : (
                // Render a default state if no mode is selected
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography variant="h6" color="textSecondary">
                    Please select a report mode from the left panel.
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ paddingX: 2, paddingTop: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                Total Rows Found: {filteredRows.length}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    </div>
  );
};

export default PurchaseAnalysisReport;
